import classNames from 'classnames';
import { memo } from 'react';

import { Copy } from '@air/next-icons';

import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

type RowProps = {
  label: string;
  value?: string;
  onCopy: (text?: string) => void;
  mono?: boolean;
  long?: boolean;
};

function Row({ label, value, onCopy, mono, long }: RowProps) {
  return (
    <div className="group flex items-center justify-between">
      <span>{label}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onCopy(value)}
          className="rounded p-1 text-grey-9 opacity-0 transition-opacity hover:bg-grey-2 hover:text-grey-11 group-hover:opacity-100"
          title={`Copy ${label}`}
        >
          <Copy className="size-3" />
        </button>
        <span className={classNames(mono ? 'font-mono' : '', long ? 'max-w-xs truncate text-12' : '', 'text-grey-11')}>
          {value ?? '—'}
        </span>
      </div>
    </div>
  );
}

type OverviewTabProps = {
  onCopy: (text?: string) => void;
};

export function getBrowserName(userAgent: string): string {
  if (userAgent.includes('Chrome') && userAgent.includes('Safari') && !userAgent.includes('Edge')) {
    return 'Chrome';
  } else if (userAgent.includes('Firefox')) {
    return 'Firefox';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    return 'Safari';
  } else if (userAgent.includes('Edge')) {
    return 'Edge';
  } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
    return 'Opera';
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    return 'Internet Explorer';
  } else {
    return 'Unknown Browser';
  }
}

/**
 * Overview tab showing user, workspace, and environment information.
 */
export const OverviewTab = memo(({ onCopy }: OverviewTabProps) => {
  const { data: account } = useAccountContext();
  const { currentWorkspace } = useCurrentWorkspace();

  return (
    <div className="h-full space-y-3 overflow-y-auto p-3">
      <div className="rounded-12 bg-grey-5 p-3">
        <h3 className="mb-2 font-semibold text-14 text-grey-12">User Info</h3>
        <div className="space-y-1 text-12 text-grey-9">
          <Row label="Email" value={account?.email} onCopy={onCopy} />
          <Row label="User ID" value={account?.id} onCopy={onCopy} />
          <Row label="Auth ID" value={account?.authId ? String(account.authId) : undefined} onCopy={onCopy} />
        </div>
      </div>

      <div className="rounded-12 bg-grey-5 p-3">
        <h3 className="mb-2 font-semibold text-14 text-grey-12">Workspace Info</h3>
        <div className="space-y-1 text-12 text-grey-9">
          <Row label="Name" value={currentWorkspace?.name} onCopy={onCopy} />
          <Row label="ID" value={currentWorkspace?.id} onCopy={onCopy} />
          <Row
            label="Plan"
            value={currentWorkspace?.plan ? String(currentWorkspace.plan) : undefined}
            onCopy={onCopy}
          />
        </div>
      </div>

      <div className="rounded-12 bg-grey-5 p-3">
        <h3 className="mb-2 font-semibold text-14 text-grey-12">Environment Info</h3>
        <div className="space-y-1 text-12 text-grey-9">
          <Row label="Environment" value={process.env.NEXT_PUBLIC_VERCEL_ENV || 'development'} onCopy={onCopy} />
          <Row
            label="URL"
            value={typeof window !== 'undefined' ? window.location.href : 'N/A'}
            onCopy={onCopy}
            mono
            long
          />
          <Row
            label="Browser"
            value={typeof navigator !== 'undefined' ? getBrowserName(navigator.userAgent) : 'N/A'}
            onCopy={onCopy}
          />
          <Row label="App Version" value={process.env.APP_VERSION || ''} onCopy={onCopy} />
        </div>
      </div>
    </div>
  );
});

OverviewTab.displayName = 'OverviewTab';
